import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Quatrefoil } from "../Ornament";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  shown: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const PostLink = ({ post, children, testid }) =>
  post.external_url ? (
    <a href={post.external_url} target="_blank" rel="noopener noreferrer" data-testid={testid} className="block">
      {children}
    </a>
  ) : (
    <Link to={`/post/${post.slug}`} data-testid={testid} className="block">
      {children}
    </Link>
  );

const BentoGrid = ({ posts }) => {
  const [first, ...rest] = posts;
  return (
    <div className="grid grid-cols-12 gap-6">
      {first && (
        <motion.div variants={reveal} className="col-span-12 lg:col-span-8">
          <PostLink post={first} testid={`post-card-${first.slug}`}>
            <div className="card-lift image-frame group bg-[#0d0e12] h-full">
              <div className="relative overflow-hidden">
                <img src={first.cover_image} alt={first.title}
                  className="w-full h-[380px] object-cover grayscale-[30%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700" />
              </div>
              <div className="p-6">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#581313] mb-2">Featured Work</p>
                <h3 className="font-serif text-2xl text-[#ede8e1] group-hover:text-[#c9c4bc] transition-colors">{first.title}</h3>
                <p className="mt-2 text-[#a8a29e] font-light">{first.excerpt}</p>
              </div>
            </div>
          </PostLink>
        </motion.div>
      )}
      <div className="col-span-12 lg:col-span-4 grid gap-6">
        {rest.slice(0, 2).map((p) => (
          <motion.div key={p.id} variants={reveal}>
            <PostLink post={p} testid={`post-card-${p.slug}`}>
              <div className="card-lift border border-[#232530] bg-[#0d0e12] p-6 h-full group">
                {p.cover_image && (
                  <div className="relative overflow-hidden mb-4">
                    <img src={p.cover_image} alt={p.title} className="w-full h-40 object-cover grayscale-[40%] group-hover:grayscale-0 transition-all duration-700" />
                  </div>
                )}
                <h3 className="font-serif text-xl text-[#ede8e1] group-hover:text-[#c9c4bc] transition-colors">{p.title}</h3>
                <p className="mt-2 text-sm text-[#a8a29e] font-light line-clamp-2">{p.excerpt}</p>
              </div>
            </PostLink>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const EditorialList = ({ posts }) => (
  <div className="border-t border-[#232530]">
    {posts.map((p, i) => (
      <motion.div key={p.id} variants={reveal}>
        <PostLink post={p} testid={`post-row-${p.slug}`}>
          <div className="group flex flex-col md:flex-row md:items-center gap-4 md:gap-10 py-8 border-b border-[#232530] hover:bg-[#0d0e12] transition-colors px-2 md:px-4">
            <span className="font-mono text-xs text-[#78716c] w-10 shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <div className="flex-1">
              <h3 className="font-serif text-xl sm:text-2xl text-[#ede8e1] group-hover:text-[#c9c4bc] group-hover:translate-x-2 transition-all duration-300">
                {p.title}
              </h3>
              <p className="mt-1 text-sm text-[#a8a29e] font-light line-clamp-1">{p.excerpt}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {(p.tags || []).slice(0, 2).map((t) => (
                <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c] border border-[#232530] px-2 py-1">{t}</span>
              ))}
              <ArrowUpRight size={18} className="text-[#581313] transition-colors" />
            </div>
          </div>
        </PostLink>
      </motion.div>
    ))}
  </div>
);

const CardGrid = ({ posts, mosaic }) => (
  <div className={`grid gap-6 ${mosaic ? "grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"}`}>
    {posts.map((p) => (
      <motion.div key={p.id} variants={reveal}>
        <PostLink post={p} testid={`post-card-${p.slug}`}>
          <div className="card-lift border border-[#232530] bg-[#0d0e12] group h-full">
            {p.cover_image && (
              <div className="relative overflow-hidden">
                <img src={p.cover_image} alt={p.title}
                  className={`w-full object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700 ${mosaic ? "h-44" : "h-52"}`} />
              </div>
            )}
            <div className="p-5">
              <h3 className="font-serif text-lg text-[#ede8e1] group-hover:text-[#c9c4bc] transition-colors leading-snug">{p.title}</h3>
              {p.excerpt && <p className="mt-2 text-sm text-[#a8a29e] font-light line-clamp-3">{p.excerpt}</p>}
              <div className="mt-4 flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {(p.tags || []).slice(0, 2).map((t) => (
                    <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#78716c]">{t}</span>
                  ))}
                </div>
                {p.external_url && <ArrowUpRight size={16} className="text-[#581313]" />}
              </div>
            </div>
          </div>
        </PostLink>
      </motion.div>
    ))}
  </div>
);

export const SectionBlock = ({ section, index }) => {
  const posts = section.posts || [];
  if (posts.length === 0) return null;
  return (
    <motion.section
      id={section.slug}
      initial="hidden" whileInView="shown" viewport={{ once: true, margin: "-80px" }}
      variants={{ shown: { transition: { staggerChildren: 0.1 } } }}
      className="relative z-10 py-20 lg:py-28"
      data-testid={`section-${section.slug}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={reveal} className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="flex items-center gap-3 font-mono text-xs tracking-[0.35em] uppercase text-[#581313] mb-4">
              <Quatrefoil size={13} /> {section.eyebrow}
            </p>
            <h2 className="font-serif font-light text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#ede8e1]">{section.title}</h2>
            <p className="mt-3 max-w-lg text-[#a8a29e] font-light">{section.description}</p>
          </div>
          <Link to={`/s/${section.slug}`} data-testid={`section-archive-link-${section.slug}`}
            className="group flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-[#78716c] hover:text-[#ede8e1] transition-colors shrink-0">
            Full Archive <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
        {section.layout === "bento" ? <BentoGrid posts={posts} />
          : section.layout === "list" ? <EditorialList posts={posts} />
          : section.layout === "mosaic" ? <CardGrid posts={posts} mosaic />
          : <CardGrid posts={posts} />}
      </div>
    </motion.section>
  );
};
